import React, { useEffect, useState } from "react";
import {
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import { tasksAPI } from "../../../api/client";
import TaskDetailPane from "../../../components/tasks/TaskDetailPane";
import TaskDashboard from "../../../components/tasks/TaskDashboard";
import InboxPanel from "../../../components/tasks/InboxPanel";
import { useInboxUnreadCount } from "../../../hooks/useInboxUnreadCount";
import SectionedTaskList from "../../../components/tasks/SectionedTaskList";
import TaskBoard from "../../../components/tasks/TaskBoard";
import SectionsEditor from "../../../components/tasks/SectionsEditor";
import TasksToolbar from "../../../components/tasks/TasksToolbar";
import useSectionedTasks, {
  readLayout,
  storeLayout,
} from "../../../components/tasks/useSectionedTasks";
import "../../../components/projects/Projects.css";
import "../../../components/tasks/Tasks.css";
import { PERMISSIONS } from "../../../constants/permissions";
import { usePermission } from "../../../hooks/usePermission";

const VIEWS = [
  { key: "tasks", label: "Tasks" },
  { key: "dashboard", label: "Dashboard" },
  { key: "inbox", label: "Inbox" },
];

const MY_TASKS = { scope: "mine" };

export default function TasksPage() {
  const { taskId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const unreadCount = useInboxUnreadCount(location.pathname);
  // Tasks is the default view; the dashboard is one click away.
  const view = taskId ? "tasks" : searchParams.get("view") || "tasks";

  const board = useSectionedTasks(MY_TASKS);
  const canCreate = usePermission(PERMISSIONS.TASK_CREATE);
  const [layout, setLayout] = useState(readLayout);
  const [editingSections, setEditingSections] = useState(false);
  const [draftRequest, setDraftRequest] = useState(null); // { sectionId, nonce }
  const [people, setPeople] = useState([]);
  const [projectOptions, setProjectOptions] = useState([]);

  useEffect(() => {
    tasksAPI
      .people()
      .then((r) => setPeople(r.data || []))
      .catch(() => {});
    tasksAPI
      .projectOptions()
      .then((r) => setProjectOptions(r.data || []))
      .catch(() => {});
  }, []);

  const openTask = (id) => navigate(`/tasks/${id}`);
  const closeTask = () => navigate("/tasks?view=tasks");
  // A pending "add task" request only applies to the view it was made in.
  const showView = (key) => {
    setDraftRequest(null);
    navigate(`/tasks?view=${key}`);
  };

  const changeLayout = (key) => {
    setLayout(key);
    setDraftRequest(null);
    storeLayout(key);
  };

  // "+ Add task" in the header opens a draft in the first section a new task
  // can go into (Overdue and Completed can't take new tasks).
  const startNewTask = () => {
    if (view !== "tasks") showView("tasks");
    const first = board.sections.find((s) => board.canAddTo(s));
    if (first) setDraftRequest({ sectionId: first.id, nonce: Date.now() });
  };

  const sharedProps = {
    groups: board.groups,
    loading: board.loading,
    selectedId: taskId,
    onOpen: openTask,
    onToggle: board.toggleComplete,
    onCreateIn: board.createIn,
    draftRequest,
  };

  return (
    <div className="projects-page tk-page">
      <div className="projects-top-bar">
        <div className="page-header">
          <h1 style={{ margin: 0 }}>My Tasks</h1>
          <div className="project-page-actions">
            {canCreate && (
              <button
                type="button"
                className="btn-primary"
                onClick={startNewTask}
              >
                + Add task
              </button>
            )}
          </div>
        </div>
        <div
          className="tk-view-tabs"
          role="tablist"
          aria-label="My Tasks views"
        >
          {VIEWS.map((v) => (
            <button
              key={v.key}
              type="button"
              role="tab"
              aria-selected={view === v.key}
              className={view === v.key ? "active" : ""}
              onClick={() => showView(v.key)}
            >
              {v.label}
              {v.key === "inbox" && unreadCount > 0 && (
                <span className="tk-tab-count">{unreadCount}</span>
              )}
            </button>
          ))}
        </div>
      </div>

      {view === "inbox" ? (
        <InboxPanel />
      ) : view === "dashboard" ? (
        <div className="tk-list-wrap">
          <TaskDashboard
            onOpenTask={openTask}
            onShowTasks={() => showView("tasks")}
          />
        </div>
      ) : (
        <div className="tk-split">
          <div
            className={`tk-list-wrap ${layout === "board" ? "tk-board-wrap" : ""}`.trim()}
          >
            <TasksToolbar
              layout={layout}
              onLayoutChange={changeLayout}
              onCustomize={() => setEditingSections(true)}
            />
            {board.error && <p className="tk-error">{board.error}</p>}
            {layout === "board" ? (
              <TaskBoard
                {...sharedProps}
                targetFor={board.targetFor}
                onMove={board.moveTask}
              />
            ) : (
              <SectionedTaskList {...sharedProps} canAddTo={board.canAddTo} />
            )}
          </div>

          {taskId && (
            <TaskDetailPane
              key={taskId}
              taskId={taskId}
              people={people}
              projectOptions={projectOptions}
              onClose={closeTask}
              onOpenTask={openTask}
              onChanged={board.loadTasks}
              onDeleted={() => {
                closeTask();
                board.loadTasks();
              }}
            />
          )}
        </div>
      )}

      {editingSections && (
        <SectionsEditor
          sections={board.sections}
          isDefault={board.sectionsAreDefault}
          onSave={board.saveSections}
          onClose={() => setEditingSections(false)}
        />
      )}
    </div>
  );
}
